import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useStore } from './store/database';
import { useEffect, useState, type ReactNode } from 'react';

// Layouts
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import Loading from './components/Loading';
import PreviewBar from './components/PreviewBar';
import type { MembershipAccess } from './core/render-policy';

// Auth pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import GatewayPage from './pages/auth/GatewayPage';
import LegalAcceptancePage from './pages/auth/LegalAcceptancePage';
import StatusPage from './pages/auth/StatusPage';
import RestoreAccessPage from './pages/auth/RestoreAccessPage';

// Public pages
import { HomePage, ProductPage, StoryPage, WaitlistPage, AllocationPage, CheckoutPage } from './pages/public/PublicPages';

// House pages
import DashboardPage from './pages/house/DashboardPage';
import ReleasesPage from './pages/house/ReleasesPage';
import ReleaseDetailPage from './pages/house/ReleaseDetailPage';
import ArchivePage from './pages/house/ArchivePage';
import ArchiveDetailPage from './pages/house/ArchiveDetailPage';
import MessagesPage from './pages/house/MessagesPage';
import EventsPage from './pages/house/EventsPage';
import InvitePage from './pages/house/InvitePage';
import MembershipPage from './pages/house/MembershipPage';
import AccountPage from './pages/house/AccountPage';

// Admin pages
import AdminDashboard, { MembersAdmin, ReleasesAdmin, ArchiveAdmin, MessagesAdmin, EventsAdmin, InvitesAdmin, CategoriesAdmin, LegalAdmin, AuditAdmin, HealthAdmin } from './pages/admin/AdminDashboard';

// Admin extensions
import { HouseControlAdmin, ContentAdmin, BrandAssetsAdmin, MediaAdmin as MediaAdminLegacy, NotificationsAdmin, OrdersAdmin, MetricsAdmin } from './pages/admin/AdminExtensions';
import { ContentBlocksAdmin, MediaLibraryAdmin, EnhancedHouseControl } from './pages/admin/ContentBlocksAdmin';

// Auth guard: requires logged in session
function AuthGuard({ children }: { children: ReactNode }) {
  const session = useStore(s => s.session);
  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// Member guard: requires active member + legal accepted
function MemberGuard({ children }: { children: ReactNode }) {
  const session = useStore(s => s.session);
  const getMember = useStore(s => s.getMember);
  const getPendingLegal = useStore(s => s.getPendingLegal);

  if (!session) return <Navigate to="/login" replace />;

  // Admin without member profile can access
  if (!session.member_id && ['super_admin', 'house_admin', 'editor', 'support'].includes(session.role)) {
    return <>{children}</>;
  }

  const member = getMember();
  if (!member) return <Navigate to="/login" replace />;

  // Status checks
  if (member.status === 'waitlist' || member.status === 'paused' || member.status === 'revoked') {
    return <Navigate to="/status" replace />;
  }

  // Legal check
  const pending = getPendingLegal(member.id);
  if (pending.length > 0) return <Navigate to="/legal" replace />;

  return <>{children}</>;
}

// Admin guard: requires admin role
function AdminGuard({ children, roles }: { children: ReactNode; roles?: string[] }) {
  const session = useStore(s => s.session);
  if (!session) return <Navigate to="/login" replace />;
  const adminRoles = roles || ['super_admin', 'house_admin', 'editor', 'support'];
  if (!adminRoles.includes(session.role)) return <Navigate to="/house" replace />;
  return <>{children}</>;
}

// Role-specific admin guard
function RoleGuard({ children, roles }: { children: ReactNode; roles: string[] }) {
  const session = useStore(s => s.session);
  if (!session || !roles.includes(session.role)) return <Navigate to="/admin" replace />;
  return <>{children}</>;
}

export default function App() {
  const init = useStore(s => s.init);
  const initialized = useStore(s => s._initialized);
  const session = useStore(s => s.session);
  const [previewAs, setPreviewAs] = useState<MembershipAccess | null>(null);

  useEffect(() => { init(); }, []);

  if (!initialized) return <Loading />;

  return (
    <BrowserRouter>
      {session?.role === 'super_admin' && <PreviewBar onPreviewChange={setPreviewAs} current={previewAs} />}
      <div className={session?.role === 'super_admin' ? 'pt-8' : ''}>
      <Routes>
        {/* Public-facing routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/product" element={<ProductPage />} />
        <Route path="/story" element={<StoryPage />} />
        <Route path="/waitlist" element={<WaitlistPage />} />
        <Route path="/allocation" element={<AuthGuard><AllocationPage /></AuthGuard>} />
        <Route path="/checkout" element={<AuthGuard><CheckoutPage /></AuthGuard>} />

        {/* Auth routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/restore-access" element={<RestoreAccessPage />} />
        <Route path="/gateway" element={<AuthGuard><GatewayPage /></AuthGuard>} />
        <Route path="/legal" element={<AuthGuard><LegalAcceptancePage /></AuthGuard>} />
        <Route path="/status" element={<AuthGuard><StatusPage /></AuthGuard>} />

        {/* Member routes */}
        <Route element={<MemberGuard><Layout /></MemberGuard>}>
          <Route path="/house" element={<DashboardPage />} />
          <Route path="/releases" element={<ReleasesPage />} />
          <Route path="/releases/:slug" element={<ReleaseDetailPage />} />
          <Route path="/archive" element={<ArchivePage />} />
          <Route path="/archive/:slug" element={<ArchiveDetailPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/invite" element={<InvitePage />} />
          <Route path="/membership" element={<MembershipPage />} />
          <Route path="/account" element={<AccountPage />} />
        </Route>

        {/* Admin routes */}
        <Route element={<AdminGuard><AdminLayout /></AdminGuard>}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/members" element={<RoleGuard roles={['super_admin', 'house_admin', 'support']}><MembersAdmin /></RoleGuard>} />
          <Route path="/admin/releases" element={<RoleGuard roles={['super_admin', 'house_admin', 'editor']}><ReleasesAdmin /></RoleGuard>} />
          <Route path="/admin/archive" element={<RoleGuard roles={['super_admin', 'house_admin', 'editor']}><ArchiveAdmin /></RoleGuard>} />
          <Route path="/admin/messages" element={<RoleGuard roles={['super_admin', 'house_admin', 'editor']}><MessagesAdmin /></RoleGuard>} />
          <Route path="/admin/events" element={<RoleGuard roles={['super_admin', 'house_admin', 'editor']}><EventsAdmin /></RoleGuard>} />
          <Route path="/admin/invites" element={<RoleGuard roles={['super_admin', 'house_admin']}><InvitesAdmin /></RoleGuard>} />
          <Route path="/admin/categories" element={<RoleGuard roles={['super_admin', 'house_admin']}><CategoriesAdmin /></RoleGuard>} />
          <Route path="/admin/legal" element={<RoleGuard roles={['super_admin', 'house_admin']}><LegalAdmin /></RoleGuard>} />
          <Route path="/admin/audit" element={<RoleGuard roles={['super_admin']}><AuditAdmin /></RoleGuard>} />
          <Route path="/admin/health" element={<RoleGuard roles={['super_admin']}><HealthAdmin /></RoleGuard>} />
          <Route path="/admin/orders" element={<RoleGuard roles={['super_admin', 'house_admin', 'support']}><OrdersAdmin /></RoleGuard>} />
          <Route path="/admin/house-control" element={<RoleGuard roles={['super_admin', 'house_admin']}><HouseControlAdmin /></RoleGuard>} />
          <Route path="/admin/content" element={<RoleGuard roles={['super_admin', 'house_admin', 'editor']}><ContentAdmin /></RoleGuard>} />
          <Route path="/admin/media" element={<RoleGuard roles={['super_admin', 'house_admin', 'editor']}><MediaAdminLegacy /></RoleGuard>} />
          <Route path="/admin/brand-assets" element={<RoleGuard roles={['super_admin', 'house_admin']}><BrandAssetsAdmin /></RoleGuard>} />
          <Route path="/admin/notifications" element={<RoleGuard roles={['super_admin', 'house_admin']}><NotificationsAdmin /></RoleGuard>} />
          <Route path="/admin/metrics" element={<RoleGuard roles={['super_admin']}><MetricsAdmin /></RoleGuard>} />
          <Route path="/admin/content-blocks" element={<RoleGuard roles={['super_admin', 'house_admin', 'editor']}><ContentBlocksAdmin /></RoleGuard>} />
          <Route path="/admin/media-library" element={<RoleGuard roles={['super_admin', 'house_admin', 'editor']}><MediaLibraryAdmin /></RoleGuard>} />
          <Route path="/admin/house" element={<RoleGuard roles={['super_admin', 'house_admin']}><EnhancedHouseControl /></RoleGuard>} />
          <Route path="/admin/product" element={<RoleGuard roles={['super_admin', 'house_admin', 'editor']}><ReleasesAdmin /></RoleGuard>} />
          <Route path="/admin/analytics" element={<RoleGuard roles={['super_admin']}><MetricsAdmin /></RoleGuard>} />
          <Route path="/admin/launch" element={<RoleGuard roles={['super_admin', 'house_admin']}><EnhancedHouseControl /></RoleGuard>} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<CatchAll />} />
      </Routes>
      </div>
    </BrowserRouter>
  );
}

function CatchAll() {
  const session = useStore(s => s.session);
  if (session) return <Navigate to="/house" replace />;
  return <Navigate to="/login" replace />;
}
